'use client';
import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { omitBy } from 'lodash-es';
import { useInfiniteQuery } from '@tanstack/react-query';

import { FilterValue, ProductDataV2, SearchProductsParams, SearchSortEnum } from '~/apis/search';

import { Button } from '@bgzt/ui';
import { SearchFilterKey, SearchFilterState } from '~/types';
import {
  useAppDidFavorite,
  useEnvironmentInfo,
  useSearchFilterSearchParamsClient,
  useSearchLog,
  useStoredRelatedKeywords,
  useStoredSearchFilters,
  useUpdateInfiniteQueryProductFavorite,
} from '~/hooks';
import { searchProductsInfiniteQueryOptions } from '~/queries';

import {
  SortFilter,
  getFilterBubbleFormattedText,
  getSelectedKeys,
  AppBar,
  FilterBottomSheet,
  FilterBubble,
  Section,
  Spacing,
  useOverlay,
  Footer,
  getIsHighlighted,
  LogVisit,
  EmptyMessage,
  ProductListTemp,
  ProductItem,
  LinkByEnvironment,
  LogClick,
  LogImpression,
} from '~/components';
import { SearchForm } from '~/app/search/_components';
import { RelatedKeywords } from '~/app/search/result/_components';
import { CONTENT_TYPE, EVENT_ACTION, EVENT_TYPE, PAGE_ID, VIEW_ID } from '~/apis/log/constant';
import { goToFullWebviewOnApp } from '~/services/app-interface';
import useDeepCompareEffect from '~/hooks/useDeepCompareEffect';
import { SearchLogParams, sendLog } from '~/apis/log';

type Props = {
  initialPageParam: SearchProductsParams;
};
export const SearchResult = ({ initialPageParam }: Props) => {
  const router = useRouter();
  const overlay = useOverlay();
  const { sendSearchLog } = useSearchLog();
  const { isWebview } = useEnvironmentInfo();

  const { searchParams, setSearchParams, hasSearchFilter } = useSearchFilterSearchParamsClient();
  const filteredSearchParams = omitBy(searchParams, (value) => (value === false ? false : !value));

  const queryOptions = searchProductsInfiniteQueryOptions(
    {
      ...initialPageParam,
      ...filteredSearchParams,
    },
    { staleTime: 3000 },
  );
  const {
    data: searchProductsData,
    fetchNextPage,
    hasNextPage,
    isFetched,
    isFetchingNextPage,
  } = useInfiniteQuery(queryOptions);

  const { updateInfiniteQueryProductFavorite } = useUpdateInfiniteQueryProductFavorite();
  useAppDidFavorite((pid, type) => {
    updateInfiniteQueryProductFavorite({
      queryKey: queryOptions.queryKey,
      pid: Number(pid),
      isFavorite: type === 'add',
    });
  });

  const searchFilters = useStoredSearchFilters(searchProductsData?.filters);
  const relatedKeywords = useStoredRelatedKeywords(
    filteredSearchParams.q as string,
    searchProductsData?.relatedKeywords,
  );

  const selectedKeys = useMemo(() => {
    return getSelectedKeys(searchParams, searchFilters);
  }, [searchParams, searchFilters]);

  const isHighlighted = useMemo(() => {
    return getIsHighlighted(selectedKeys.keys, searchFilters);
  }, [selectedKeys, searchFilters]);

  const handleSearchInputClick = () => {
    const endcodeKeyword = encodeURIComponent(searchParams.q ?? '');
    router.push(`/search?q=${endcodeKeyword}`);
  };

  const handleSubmitFilter = (filter: SearchFilterState) => {
    setSearchParams({ ...filter, categoryId: filter.categoryId ? [filter?.categoryId] : null });
  };

  const handleSortChange = (value?: string) => {
    setSearchParams({ sort: value as SearchSortEnum });
  };

  const handleSuggestedKeywordClick = (keyword: string) => {
    const endcodeKeyword = encodeURIComponent(keyword);
    const path = `/search/result?q=${endcodeKeyword}`;
    const url = isWebview ? process.env.NEXT_PUBLIC_DOMAIN + path : path;
    isWebview ? goToFullWebviewOnApp({ url }).go() : globalThis.window.open(url, '_blank');
  };

  const handleMoreViewClick = async () => {
    await fetchNextPage();
  };

  const openFilterBottomSheet = (scrollTarget: string = '') => {
    overlay.open(({ isOpen, close }) => (
      <FilterBottomSheet
        isOpen={isOpen}
        onClose={() => {
          close();
        }}
        onSubmit={handleSubmitFilter}
        data={filteredSearchParams}
        scrollTarget={scrollTarget}
        filters={searchFilters ?? []}
      />
    ));
  };

  const productData = searchProductsData?.pages as ProductDataV2[];
  const hasProductData = productData?.length > 0;
  const sortFilter = searchFilters?.find((filter) => filter.id === 'sort')?.values || [];
  const selectedSortValue = searchParams.sort ?? sortFilter.find((data: FilterValue) => data._default)?.value ?? 'date';
  const displayFilter = hasSearchFilter || hasProductData;

  useDeepCompareEffect(() => {
    if (!searchProductsData) return;
    const logParams: SearchLogParams = {
      event_type: EVENT_TYPE.SEARCH_RESPONSE,
      event_action: EVENT_ACTION.SEARCH_RESPONSE,
      page_id: PAGE_ID.SEARCH_RESULT,
      search_term: filteredSearchParams.q as string,
      page_number: searchProductsData.page,
      total_elements: searchProductsData.totalElements,
      search_options: filteredSearchParams,
      search_option_view: '3열',
    };
    sendSearchLog(logParams);
  }, [searchProductsData]);

  return (
    <>
      <LogVisit
        params={{
          event_type: EVENT_TYPE.VISIT,
          event_action: EVENT_ACTION.VISIT,
          page_id: hasProductData ? PAGE_ID.SEARCH_RESULT : PAGE_ID.SEARCH_RESULT_EMPTY,
          ref_term: searchParams.q,
        }}
      />
      <AppBar
        slot={<SearchForm value={searchParams.q ?? ''} onClick={handleSearchInputClick} readOnly />}
        actions={['home']}
      />

      {hasProductData && relatedKeywords && (
        <RelatedKeywords keywords={relatedKeywords.map((k) => k.name)} onClickKeyword={handleSuggestedKeywordClick} />
      )}

      {displayFilter && searchFilters && (
        <>
          <FilterBubble
            data={searchFilters
              ?.filter(({ shortcut }) => shortcut)
              //   price는 특이하게 key값이 없습니다 (아마, maxPrice, minPrice로 처리를 해서 그런거 같습니다)
              .map(({ key, name, id }) => {
                if (id === 'price') {
                  return {
                    key: 'price',
                    placeholderLabel: name,
                    onClick: () => {
                      openFilterBottomSheet('price');
                    },
                    renderLabel: (value) =>
                      getFilterBubbleFormattedText(id, [value[0] ?? '', value[1] ?? ''], searchFilters ?? []),
                    value: [searchParams.minPrice ?? '', searchParams.maxPrice ?? ''],
                    showsPlaceholder: !searchParams?.minPrice && !searchParams?.maxPrice,
                  };
                }

                if (key === 'modelId' || key === 'brandId') {
                  return {
                    key,
                    placeholderLabel: name,
                    onClick: () => {
                      openFilterBottomSheet(key);
                    },
                    renderLabel: (value) => getFilterBubbleFormattedText(key, value, searchFilters ?? []),
                    value: searchParams?.brandId ?? [],
                    showsPlaceholder: searchParams?.brandId?.length === 0,
                  };
                }

                return {
                  key: key ?? id,
                  placeholderLabel: name,
                  onClick: () => {
                    openFilterBottomSheet(key ?? id);
                  },
                  renderLabel: (value) => getFilterBubbleFormattedText(key ?? id, value, searchFilters ?? []),
                  value: (key ?? id) in searchParams ? [searchParams[(key ?? id) as SearchFilterKey]] : [],
                };
              })}
            selectedKeyShortcutKeys={selectedKeys.shortcutKeys}
            onFilterIconClick={openFilterBottomSheet}
            isHighlighted={isHighlighted}
          />
          <Spacing height={8} />

          <SortFilter
            totalAmount={searchProductsData?.totalElements ?? 0}
            sortData={sortFilter}
            selectedValue={selectedSortValue}
            onSortChange={handleSortChange}
          />
        </>
      )}

      <Spacing height={12} />

      <EmptyMessage
        display={isFetched && productData?.length < 1}
        q={searchParams?.q ?? ''}
        hasFilter={hasSearchFilter}
        guide={searchProductsData?.guide}
        isBlockedKeyword={searchProductsData?.responseCode === 'BLOCKED_KEYWORD'}
      />

      <Section>
        <ProductListTemp col={3}>
          {productData?.map((product, index) => {
            const logParams = {
              page_id: PAGE_ID.SEARCH_RESULT,
              view_id: VIEW_ID.SEARCH_PRODUCT,
              content_id: String(product.pid),
              content_type: CONTENT_TYPE.MERCARI,
              content_position: index,
              ref_term: searchParams?.q,
              tracking: product?.tracking,
            };

            const endcodeKeyword = encodeURIComponent(searchParams.q ?? '');
            const path = `/products/${product.pid}`;
            const query = `?ref_page=${PAGE_ID.SEARCH_RESULT}&ref_position=${index}&ref_term=${endcodeKeyword}`;

            return (
              <li key={product.pid}>
                <LogImpression
                  params={{
                    ...logParams,
                    event_type: EVENT_TYPE.IMPRESSION,
                    event_action: EVENT_ACTION.IMPRESSION,
                    content_status: product?.status,
                  }}
                >
                  <LogClick
                    params={{
                      ...logParams,
                      event_type: EVENT_TYPE.SELECT_PRODUCT,
                      event_action: EVENT_ACTION.SELECT_PRODUCT,
                    }}
                  >
                    <LinkByEnvironment href={path + query} target={'_blank'}>
                      <ProductItem
                        image={product?.productImage ?? ''}
                        name={product?.name ?? ''}
                        price={product.price ?? 0}
                        updatedBefore={product?.updatedBefore ?? ''}
                        pid={product?.pid ?? 0}
                        status={product?.status}
                        faved={product?.faved}
                        location={product?.location}
                        discountRate={product?.discount?.rate}
                        viewType="3-column"
                        onFavoriteClick={(value) => {
                          sendLog({
                            ...logParams,
                            event_type: EVENT_TYPE.WISHLIST,
                            event_action: value ? EVENT_ACTION.ADD_TO_WISHLIST : EVENT_ACTION.REMOVE_FROM_WISHLIST,
                          });
                        }}
                      />
                    </LinkByEnvironment>
                  </LogClick>
                </LogImpression>
              </li>
            );
          })}
        </ProductListTemp>
      </Section>

      {hasProductData && hasNextPage && (
        <Section>
          <Spacing height={20} />
          <Button
            variant="normalGray"
            size="large"
            width={'100%'}
            onClick={handleMoreViewClick}
            isLoading={isFetchingNextPage}
          >
            더보기
          </Button>
        </Section>
      )}

      {hasProductData && (
        <>
          <Spacing height={40} />
          <Footer />
        </>
      )}
    </>
  );
};
